/**
 * Gemeinsame Bild-Komprimierungs-Utilities für PDF und E-Mail Versand.
 * Reduziert Auflösung auf eine maximale Kantenlänge und re-encodiert als JPEG.
 */

export const DEFAULT_MAX_SIZE = 1024;
export const DEFAULT_QUALITY = 0.8;

/**
 * Verkleinert ein Bild (Data-URL oder Object-URL) auf eine maximale
 * Kantenlänge und gibt eine komprimierte JPEG Data-URL zurück.
 */
export async function compressImageSrc(
  src: string,
  maxSize: number = DEFAULT_MAX_SIZE,
  quality: number = DEFAULT_QUALITY
): Promise<string> {
  const img = await loadImage(src);
  const canvas = drawToCanvas(img, maxSize);
  if (!canvas) throw new Error("Canvas konnte nicht erstellt werden (kein 2D-Kontext oder ungültige Bildgröße)");
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Standardwerte für clientseitige Upload-Komprimierung
 * (vor dem Hochladen vom Handy / Browser zum Server).
 */
export const UPLOAD_MAX_SIZE = 2000;
export const UPLOAD_QUALITY = 0.8;

/**
 * Verkleinert eine vom Nutzer ausgewählte Bilddatei vor dem Upload auf
 * eine maximale Kantenlänge und re-encodiert sie als JPEG. Nicht-Bilder
 * werden unverändert zurückgegeben. Falls die Komprimierung fehlschlägt
 * oder das Ergebnis größer wäre als das Original, wird die Originaldatei
 * zurückgegeben.
 */
export async function compressImageFile(
  file: File,
  maxSize: number = UPLOAD_MAX_SIZE,
  quality: number = UPLOAD_QUALITY
): Promise<File> {
  if (!file.type.startsWith("image/")) {
    return file;
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const canvas = drawToCanvas(img, maxSize);
    if (!canvas) return file;

    const compressed = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
    );
    if (!compressed) return file;
    if (compressed.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^/.]+$/, "");
    return new File([compressed], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch (error) {
    console.warn("Bild-Datei-Komprimierung fehlgeschlagen, nutze Original:", error);
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Verkleinert einen Bild-Blob auf eine maximale Kantenlänge und gibt
 * einen komprimierten JPEG-Blob zurück. Nicht-Bild-Blobs werden unverändert
 * zurückgegeben.
 */
export async function compressImageBlob(
  blob: Blob,
  maxSize: number = DEFAULT_MAX_SIZE,
  quality: number = DEFAULT_QUALITY
): Promise<Blob> {
  if (!blob.type.startsWith("image/")) {
    return blob;
  }

  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const canvas = drawToCanvas(img, maxSize);
    if (!canvas) return blob;

    const compressed = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
    );
    if (!compressed) return blob;
    return compressed.size < blob.size ? compressed : blob;
  } catch (error) {
    console.warn("Bild-Blob-Komprimierung fehlgeschlagen, nutze Original:", error);
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function drawToCanvas(img: HTMLImageElement, maxSize: number): HTMLCanvasElement | null {
  const { width, height } = img;
  if (!width || !height) return null;

  const scale = Math.min(1, maxSize / Math.max(width, height));
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);
  return canvas;
}
